import React, { useState, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Public pages — eagerly loaded for fast initial render
import WaitlistLanding from './pages/WaitlistLanding/WaitlistLandingOptimized';
import Landing from './pages/Landing/LandingOptimized';
import BetaAccess from './pages/BetaAccess/BetaAccess';
import TermsOfUse from './pages/Legal/TermsOfUse';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';

import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { PlanProvider } from './contexts/PlanContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import FeedbackSelectionOverlay from './components/feedback/FeedbackSelectionOverlay';
import FeedbackModal from './components/feedback/FeedbackModal';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './styles/App.css';

// Post-login pages — lazy loaded to reduce initial bundle (~357KB savings)
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Subscriptions = React.lazy(() => import('./pages/Subscriptions/Subscriptions'));
const AnalyticsPage = React.lazy(() => import('./pages/Analytics/Analytics'));
const AIInsights = React.lazy(() => import('./pages/AIInsights'));
const Community = React.lazy(() => import('./pages/Community/Community'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const Notifications = React.lazy(() => import('./pages/Notifications/Notifications'));
const Onboarding = React.lazy(() => import('./pages/Onboarding/Onboarding'));
const Login = React.lazy(() => import('./pages/Login/Login'));
const Signup = React.lazy(() => import('./pages/Signup/Signup'));
const GmailCallback = React.lazy(() => import('./pages/GmailCallback/GmailCallback'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess/PaymentSuccess'));

// Admin is QA-only — lazy load and only render in QA environment
const isQA = window.location.hostname.includes('qa.') || window.location.hostname === 'localhost';
const Admin = isQA ? React.lazy(() => import('./pages/Admin/Admin')) : null;

function App() {
  const [activeTab, setActiveTab] = useState('home');

  return (
    <AuthProvider>
      <div className="App">
        <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 36, height: 36, border: '3px solid #E5E7EB', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/gmail-callback" element={<GmailCallback />} />

            {/* Payment Success - needs PlanProvider and auth */}
            <Route
              path="/payment-success"
              element={
                <PlanProvider>
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                </PlanProvider>
              }
            />

            {/* Onboarding - minimal context loading */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Dashboard and other routes - full context loading */}
            <Route
              path="/dashboard"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <Dashboard activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <Subscriptions activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/analytics"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <AnalyticsPage activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/ai-insights"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <AIInsights activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/community"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <Community activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/settings"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <Settings activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />
            <Route
              path="/notifications"
              element={
                <PlanProvider>
                  <SettingsProvider>
                    <NotificationProvider>
                      <SubscriptionProvider>
                        <FeedbackProvider>
                          <FeedbackSelectionOverlay />
                          <FeedbackModal />
                          <ProtectedRoute>
                            <Notifications activeTab={activeTab} setActiveTab={setActiveTab} />
                          </ProtectedRoute>
                        </FeedbackProvider>
                      </SubscriptionProvider>
                    </NotificationProvider>
                  </SettingsProvider>
                </PlanProvider>
              }
            />

            {/* Admin Route (QA-only — not loaded in production, requires auth) */}
            {Admin && <Route path="/admin" element={<React.Suspense fallback={<div>Loading...</div>}><ProtectedRoute><Admin /></ProtectedRoute></React.Suspense>} />}

            {/* Default Routes */}
            <Route path="/" element={<WaitlistLanding />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/beta" element={<BetaAccess />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      <Analytics />
      <SpeedInsights />
    </AuthProvider>
  );
}

export default App;